import React, { useState } from "react";
import { ChevronDown, X } from "lucide-react";
import * as DropdownMenu from "@radix-ui/react-dropdown-menu";
import {
  Button,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  ModalTitle,
} from "../../../shared/ui";

interface RejectModalProps {
  open: boolean;
  onClose: () => void;
  onConfirm: (reason: string) => void;
  customerName?: string;
  isSubmitting?: boolean;
}

const REJECTION_REASONS = [
  "Rate discrepancy too high",
  "Insufficient treasury liquidity",
  "Compliance / KYC restriction",
  "Customer requested cancellation",
  "Market volatility threshold exceeded",
  "Other",
];

export const RejectModal: React.FC<RejectModalProps> = ({
  open,
  onClose,
  onConfirm,
  customerName,
  isSubmitting = false,
}) => {
  const [selectedReason, setSelectedReason] = useState(REJECTION_REASONS[0]);
  const [customNote, setCustomNote] = useState("");

  const handleClose = () => {
    if (isSubmitting) return;
    onClose();
    setSelectedReason(REJECTION_REASONS[0]);
    setCustomNote("");
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const finalReason = customNote ? `${selectedReason}: ${customNote}` : selectedReason;
    onConfirm(finalReason);
  };

  return (
    <Modal
      open={open}
      onOpenChange={handleClose}
      overlayClassName="bg-black/75"
      contentClassName="max-w-[500px] bg-white rounded-[24px]"
    >
      <ModalHeader className="items-center justify-between pb-3 border-b border-[#EBEDF9]">
        <ModalTitle className="text-[20px] font-[600] text-[#161827]">
          Reject OTC Request
        </ModalTitle>
        <button
          type="button"
          aria-label="Close modal"
          onClick={handleClose}
          className="rounded-full p-2 text-[#161827] hover:bg-[#F2F4FF] transition-colors"
        >
          <X size={20} />
        </button>
      </ModalHeader>

      <form onSubmit={handleSubmit}>
        <ModalBody className="flex flex-col gap-6 py-6">
          <p className="text-[14px] text-[#4F5372]">
            Are you sure you want to reject this OTC request for{" "}
            <strong className="text-[#161827]">{customerName || "this customer"}</strong>? This will notify the customer and cancel the transaction.
          </p>

          {/* Rejection Reason Dropdown */}
          <div className="flex flex-col gap-2">
            <p className="text-[14px] font-[450] text-[#2C2F44]">
              Rejection reason
            </p>
            <DropdownMenu.Root>
              <DropdownMenu.Trigger asChild>
                <button
                  type="button"
                  className="flex h-[48px] w-full items-center justify-between rounded-[8px] bg-[#E9ECF8] px-4 text-left transition-colors hover:bg-[#E2E6F5] focus:outline-none"
                >
                  <span className="text-[14px] font-[400] text-[#161827] truncate">
                    {selectedReason}
                  </span>
                  <ChevronDown size={18} className="text-[#6F739B] shrink-0" />
                </button>
              </DropdownMenu.Trigger>
              <DropdownMenu.Portal>
                <DropdownMenu.Content
                  align="start"
                  sideOffset={6}
                  className="z-[125] max-h-[240px] w-[var(--radix-dropdown-menu-trigger-width)] overflow-y-auto rounded-[12px] border border-[#E1E6FF] bg-white p-1.5 shadow-lg"
                >
                  {REJECTION_REASONS.map((reason) => {
                    const isSelected = selectedReason === reason;
                    return (
                      <DropdownMenu.Item
                        key={reason}
                        onSelect={() => setSelectedReason(reason)}
                        className={`flex items-center cursor-pointer rounded-[8px] px-3.5 py-2.5 text-[14px] outline-none transition-colors focus:bg-[#F5F6FD] ${
                          isSelected
                            ? "bg-[#F1ECFF] text-[#5E01D6] font-[500]"
                            : "text-[#383B56]"
                        }`}
                      >
                        <span className="truncate">{reason}</span>
                      </DropdownMenu.Item>
                    );
                  })}
                </DropdownMenu.Content>
              </DropdownMenu.Portal>
            </DropdownMenu.Root>
          </div>

          {/* Optional Note */}
          <div className="flex flex-col gap-2">
            <p className="text-[14px] font-[450] text-[#2C2F44]">
              Additional remark <span className="font-normal italic text-[#6F739B]">• Optional</span>
            </p>
            <textarea
              rows={3}
              value={customNote}
              onChange={(e) => setCustomNote(e.target.value)}
              placeholder="Add reason details for internal logs..."
              className="min-h-[100px] w-full resize-none rounded-[8px] bg-[#E9ECF8] p-4 text-[14px] text-[#161827] placeholder:text-[#9A9CC0] focus:outline-none focus:ring-2 focus:ring-[#D9DEFF]"
            />
          </div>
        </ModalBody>

        <ModalFooter className="flex items-center justify-end gap-3 border-t border-[#EBEDF9] px-6 py-4">
          <button
            type="button"
            onClick={handleClose}
            disabled={isSubmitting}
            className="h-10 rounded-[8px] px-4 py-2 text-[14px] font-medium text-[#344054] hover:bg-[#F5F6FD] transition-colors disabled:opacity-50"
          >
            Cancel
          </button>
          <Button
            type="submit"
            variant="danger"
            disabled={isSubmitting}
            className="px-6"
          >
            {isSubmitting ? "Rejecting..." : "Reject request"}
          </Button>
        </ModalFooter>
      </form>
    </Modal>
  );
};
